/**
 * Australian Market Prices reference table, VERBATIM port from
 * iOS `frontend/app/tools/cost-tracker.tsx:313`.
 *
 * 250+ Australian SA-market prices keyed by line-item name. Used to
 * render instant "Typical: $XXX-$XXX" hints under each item BEFORE
 * the AI validation API call kicks in. The min/max ranges drive the
 * inline market hint in the expense form modal (matching iOS).
 *
 * If you need to update prices, update iOS first, then re-port here.
 */

export const AUSTRALIAN_MARKET_PRICES = {
  // Site & Surveys
  'Soil test': { min: 300, max: 800, description: 'Geotechnical soil testing for foundation design.' },
  'Geotechnical report': { min: 500, max: 1500, description: 'Detailed soil analysis report.' },
  'Feature and level survey': { min: 400, max: 1200, description: 'Survey showing site features and levels.' },
  'Contour survey': { min: 400, max: 1000, description: 'Topographical survey showing land contours.' },
  'Site survey': { min: 300, max: 800, description: 'Basic site boundary survey.' },
  
  // Site Preparation
  'Demolition': { min: 10000, max: 50000, description: 'Demolition of existing structures.' },
  'Tree removal': { min: 500, max: 5000, description: 'Per tree, depends on size and access.' },
  'Temporary fencing': { min: 500, max: 2000, description: 'Site security fencing during construction.' },
  'Site clearing': { min: 2000, max: 15000, description: 'Clearing vegetation and debris.' },
  'Earthworks': { min: 5000, max: 30000, description: 'Site leveling and earth moving.' },
  'Site cut': { min: 5000, max: 40000, description: 'Cutting into sloped land.' },
  'Fill removal': { min: 2000, max: 20000, description: 'Removing excess soil from site.' },
  'Retaining walls': { min: 3000, max: 30000, description: 'Structural walls to retain soil.' },
  'Stormwater management': { min: 2000, max: 15000, description: 'On-site stormwater systems.' },
  
  // Connections
  'Stormwater connection': { min: 1500, max: 8000, description: 'Connection to council stormwater.' },
  'Sewer connection': { min: 2000, max: 15000, description: 'Connection to sewer mains.' },
  'Water connection': { min: 1000, max: 5000, description: 'Connection to water mains.' },
  'Gas connection': { min: 500, max: 3000, description: 'Connection to gas mains.' },
  'Electrical connection': { min: 1000, max: 5000, description: 'Connection to power grid.' },
  'NBN connection': { min: 200, max: 1000, description: 'NBN/internet infrastructure.' },
  'Footpath crossover': { min: 1500, max: 5000, description: 'Council footpath crossing.' },
  'Driveway crossover': { min: 2000, max: 8000, description: 'Vehicle crossing to street.' },
  
  // Approvals & Design
  'Development approval': { min: 2000, max: 20000, description: 'DA fees and assessments.' },
  'Planning approval': { min: 1000, max: 10000, description: 'Planning permits and fees.' },
  'Building rules consent': { min: 500, max: 3000, description: 'Building code compliance.' },
  'Engineering drawings': { min: 1500, max: 8000, description: 'Structural engineering plans.' },
  'Energy rating assessment': { min: 500, max: 2000, description: 'NatHERS energy rating.' },
  'Bushfire report': { min: 500, max: 3000, description: 'BAL assessment report.' },
  'Flood report': { min: 500, max: 2000, description: 'Flood risk assessment.' },
  'Private certifier': { min: 2000, max: 8000, description: 'Building certification fees.' },
  'Water authority approval': { min: 500, max: 2000, description: 'Water authority fees.' },
  'Drafting': { min: 2000, max: 15000, description: 'House plan drafting.' },
  'Architectural design': { min: 5000, max: 50000, description: 'Custom architectural design.' },
  'Interior design consultation': { min: 1000, max: 10000, description: 'Interior design services.' },
  'Structural engineering': { min: 2000, max: 10000, description: 'Structural engineering fees.' },
  'Civil engineering': { min: 1500, max: 8000, description: 'Civil works engineering.' },
  'Building consultant': { min: 500, max: 3000, description: 'Professional building advice.' },
  'Builder advisory service': { min: 300, max: 2000, description: 'Expert builder consultation.' },
  
  // Legal & Finance
  'Conveyancing': { min: 800, max: 2500, description: 'Legal property transfer.' },
  'Legal review': { min: 500, max: 2000, description: 'Contract legal review.' },
  'Finance fees': { min: 500, max: 3000, description: 'Loan establishment fees.' },
  'Loan application fees': { min: 300, max: 1000, description: 'Mortgage application costs.' },
  'Lenders mortgage insurance': { min: 5000, max: 30000, description: 'LMI if deposit under 20%.' },
  'Construction insurance': { min: 1500, max: 5000, description: 'Builder risk insurance.' },
  
  // Pre-Construction
  'Temporary site power': { min: 500, max: 2000, description: 'Temporary electrical supply.' },
  'Temporary water supply': { min: 300, max: 1000, description: 'Temporary water connection.' },
  
  // Structure
  'Slab': { min: 15000, max: 50000, description: 'Concrete slab foundation.' },
  'Footings': { min: 5000, max: 20000, description: 'Foundation footings.' },
  'Structural steel': { min: 5000, max: 30000, description: 'Steel structural elements.' },
  'Framing': { min: 20000, max: 80000, description: 'Wall and roof framing.' },
  'Upper floor framing': { min: 10000, max: 40000, description: 'Second storey floor frame.' },
  'Roof structure': { min: 8000, max: 35000, description: 'Roof framing and trusses.' },
  
  // External Walls
  'Brickwork': { min: 15000, max: 60000, description: 'Brick external walls.' },
  'Render': { min: 8000, max: 30000, description: 'Rendered finish to walls.' },
  'Cladding': { min: 10000, max: 40000, description: 'External wall cladding.' },
  'Feature stone': { min: 3000, max: 20000, description: 'Decorative stone features.' },
  'Feature timber': { min: 2000, max: 15000, description: 'Decorative timber cladding.' },
  'Façade upgrade': { min: 5000, max: 30000, description: 'Premium façade treatments.' },
  
  // Doors & Entry
  'Garage door': { min: 1500, max: 8000, description: 'Automatic garage door.' },
  'Front door upgrade': { min: 1000, max: 8000, description: 'Premium entry door.' },
  
  // Roofing
  'Roof tiles': { min: 8000, max: 30000, description: 'Concrete or terracotta tiles.' },
  'Colorbond roofing': { min: 6000, max: 25000, description: 'Metal Colorbond roof.' },
  'Roof sarking': { min: 1000, max: 4000, description: 'Roof membrane/sarking.' },
  'Roof insulation': { min: 2000, max: 8000, description: 'Roof space insulation.' },
  'Guttering': { min: 1500, max: 6000, description: 'Gutters and fascia.' },
  'Downpipes': { min: 500, max: 2000, description: 'Roof drainage downpipes.' },
  
  // Windows & Doors
  'Aluminium windows': { min: 8000, max: 35000, description: 'Standard aluminium windows.' },
  'Double glazing': { min: 5000, max: 25000, description: 'Double glazed window upgrade.' },
  'Sliding doors': { min: 2000, max: 10000, description: 'Aluminium sliding doors.' },
  'Bi fold doors': { min: 5000, max: 20000, description: 'Bi-fold stacking doors.' },
  'Stacker doors': { min: 4000, max: 15000, description: 'Multi-panel stacker doors.' },
  'Security screens': { min: 2000, max: 10000, description: 'Security mesh screens.' },
  'Flyscreens': { min: 500, max: 3000, description: 'Insect screens for windows.' },
  
  // Insulation
  'Wall insulation': { min: 2000, max: 8000, description: 'Wall cavity insulation.' },
  'Ceiling insulation': { min: 1500, max: 6000, description: 'Ceiling batts insulation.' },
  'Acoustic insulation': { min: 1000, max: 5000, description: 'Sound dampening insulation.' },
  
  // Electrical
  'Downlights': { min: 1000, max: 5000, description: 'LED downlight package.' },
  'Pendant lights': { min: 500, max: 5000, description: 'Decorative pendant lighting.' },
  'Feature lighting': { min: 1000, max: 8000, description: 'Statement lighting fixtures.' },
  'Exterior lighting': { min: 500, max: 4000, description: 'Outdoor lighting package.' },
  'Power points': { min: 500, max: 3000, description: 'Additional power outlets.' },
  'USB outlets': { min: 200, max: 1000, description: 'USB charging outlets.' },
  'TV points': { min: 200, max: 800, description: 'TV antenna/cable points.' },
  'Data points': { min: 300, max: 1500, description: 'Ethernet data points.' },
  'Switchboard upgrade': { min: 500, max: 2500, description: 'Electrical switchboard.' },
  'Smart home wiring': { min: 2000, max: 15000, description: 'Home automation wiring.' },
  
  // Security
  'Security system': { min: 1500, max: 8000, description: 'Alarm system installed.' },
  'CCTV': { min: 1000, max: 6000, description: 'Security camera system.' },
  'Intercom': { min: 500, max: 3000, description: 'Video intercom system.' },
  'Doorbell': { min: 200, max: 800, description: 'Smart doorbell camera.' },
  
  // Solar & Energy
  'Solar system': { min: 4000, max: 15000, description: '6.6kW solar panel system.' },
  'Solar battery': { min: 8000, max: 20000, description: 'Battery storage system.' },
  
  // Hot Water
  'Hot water system': { min: 1500, max: 5000, description: 'Standard hot water unit.' },
  'Gas hot water': { min: 1200, max: 4000, description: 'Gas hot water system.' },
  'Electric hot water': { min: 800, max: 2500, description: 'Electric hot water system.' },
  'Heat pump hot water': { min: 3000, max: 6000, description: 'Heat pump hot water.' },
  
  // Water Systems
  'Water filtration system': { min: 500, max: 3000, description: 'Whole house water filter.' },
  'Rainwater tank': { min: 2000, max: 8000, description: 'Rainwater collection tank.' },
  'External taps': { min: 200, max: 800, description: 'Garden tap points.' },
  
  // Heating & Cooling
  'Ducted air conditioning': { min: 10000, max: 25000, description: 'Ducted AC system.' },
  'Split system air conditioning': { min: 1500, max: 4000, description: 'Per split system unit.' },
  'Evaporative cooling': { min: 3000, max: 8000, description: 'Evaporative cooler.' },
  'Gas heating': { min: 3000, max: 10000, description: 'Ducted gas heating.' },
  'Electric heating': { min: 1000, max: 5000, description: 'Electric heating system.' },
  'Underfloor heating': { min: 3000, max: 15000, description: 'In-floor heating.' },
  'Ceiling fans': { min: 300, max: 1500, description: 'Per ceiling fan installed.' },
  
  // Kitchen
  'Kitchen cabinetry': { min: 10000, max: 40000, description: 'Kitchen cabinet package.' },
  'Soft close drawers': { min: 500, max: 2000, description: 'Soft close drawer upgrade.' },
  'Stone benchtops': { min: 3000, max: 15000, description: 'Natural or engineered stone.' },
  'Laminate benchtops': { min: 1000, max: 4000, description: 'Laminate bench surfaces.' },
  'Waterfall benchtop': { min: 1500, max: 5000, description: 'Waterfall edge detail.' },
  'Appliance upgrades': { min: 2000, max: 15000, description: 'Premium appliance package.' },
  'Oven upgrade': { min: 500, max: 4000, description: 'Premium oven upgrade.' },
  'Cooktop upgrade': { min: 500, max: 3000, description: 'Induction/gas cooktop.' },
  'Rangehood': { min: 500, max: 3000, description: 'Kitchen rangehood.' },
  'Dishwasher': { min: 600, max: 2500, description: 'Dishwasher supply/install.' },
  'Microwave provision': { min: 200, max: 800, description: 'Built-in microwave space.' },
  'Butlers pantry': { min: 5000, max: 20000, description: 'Butler pantry fitout.' },
  'Pantry fit out': { min: 1000, max: 5000, description: 'Pantry shelving/storage.' },
  'Splashback': { min: 1000, max: 5000, description: 'Kitchen splashback.' },
  
  // Bathroom
  'Bathroom tiles': { min: 2000, max: 10000, description: 'Bathroom wall/floor tiles.' },
  'Full height tiling': { min: 1500, max: 6000, description: 'Floor to ceiling tiles.' },
  'Shower niche': { min: 200, max: 800, description: 'Recessed shower shelf.' },
  'Frameless shower screen': { min: 800, max: 3000, description: 'Frameless glass screen.' },
  'Semi frameless shower': { min: 500, max: 1500, description: 'Semi-frameless screen.' },
  'Freestanding bath': { min: 1500, max: 8000, description: 'Freestanding bathtub.' },
  'Vanity upgrade': { min: 800, max: 4000, description: 'Premium bathroom vanity.' },
  'Stone vanity top': { min: 500, max: 2500, description: 'Stone vanity benchtop.' },
  'Tapware upgrade': { min: 500, max: 3000, description: 'Premium tap/mixer set.' },
  'Rainfall shower': { min: 300, max: 1500, description: 'Rainfall showerhead.' },
  'Heated towel rails': { min: 300, max: 1200, description: 'Heated towel rail.' },
  'Bathroom mirrors': { min: 200, max: 1500, description: 'Bathroom mirror.' },
  'Shaving cabinet': { min: 300, max: 1500, description: 'Mirror cabinet.' },
  
  // Flooring
  'Carpet': { min: 3000, max: 12000, description: 'Carpet supply and install.' },
  'Timber flooring': { min: 8000, max: 30000, description: 'Hardwood timber floors.' },
  'Hybrid flooring': { min: 4000, max: 15000, description: 'Hybrid plank flooring.' },
  'Vinyl plank flooring': { min: 3000, max: 10000, description: 'Vinyl plank floors.' },
  'Floor tiling': { min: 4000, max: 20000, description: 'Floor tile installation.' },
  'Feature floor tiles': { min: 1000, max: 5000, description: 'Decorative floor tiles.' },
  'Skirting upgrade': { min: 500, max: 3000, description: 'Premium skirting boards.' },
  
  // Painting & Finishes
  'Internal painting': { min: 5000, max: 20000, description: 'Interior paint package.' },
  'External painting': { min: 3000, max: 15000, description: 'Exterior paint package.' },
  'Feature wall paint': { min: 500, max: 2000, description: 'Accent wall painting.' },
  'Wallpaper': { min: 500, max: 5000, description: 'Wallpaper supply/install.' },
  'Decorative mouldings': { min: 1000, max: 8000, description: 'Cornices and mouldings.' },
  'Wainscoting': { min: 2000, max: 10000, description: 'Wall panelling detail.' },
  
  // Outdoor Living
  'Decking': { min: 5000, max: 30000, description: 'Timber or composite deck.' },
  'Veranda': { min: 8000, max: 35000, description: 'Covered veranda area.' },
  'Pergola': { min: 5000, max: 25000, description: 'Outdoor pergola structure.' },
  'Alfresco upgrade': { min: 10000, max: 50000, description: 'Premium outdoor living.' },
  'Outdoor kitchen': { min: 5000, max: 30000, description: 'BBQ and outdoor kitchen.' },
  'Outdoor blinds': { min: 2000, max: 10000, description: 'Café blinds or screens.' },
  'Outdoor ceiling heaters': { min: 1000, max: 5000, description: 'Outdoor heating.' },
  
  // Landscaping
  'Turf': { min: 2000, max: 10000, description: 'Lawn turf installation.' },
  'Artificial grass': { min: 3000, max: 15000, description: 'Synthetic turf.' },
  'Irrigation system': { min: 1500, max: 8000, description: 'Garden irrigation.' },
  'Garden beds': { min: 1000, max: 8000, description: 'Garden bed construction.' },
  'Planting': { min: 1000, max: 10000, description: 'Plants and installation.' },
  'Mulch': { min: 500, max: 2000, description: 'Garden mulch delivery.' },
  'Garden edging': { min: 500, max: 3000, description: 'Garden border edging.' },
  'Concrete paths': { min: 2000, max: 10000, description: 'Concrete pathways.' },
  'Exposed aggregate driveway': { min: 5000, max: 20000, description: 'Decorative driveway.' },
  'Paving': { min: 3000, max: 15000, description: 'Paver installation.' },
  'Fencing': { min: 3000, max: 20000, description: 'Boundary fencing.' },
  'Side gates': { min: 500, max: 3000, description: 'Side access gates.' },
  
  // Pool & Spa
  'Pool': { min: 30000, max: 100000, description: 'Swimming pool installation.' },
  'Spa': { min: 8000, max: 25000, description: 'Spa/hot tub installation.' },
  'Pool fencing': { min: 3000, max: 15000, description: 'Pool safety fencing.' },
  'Pool heating': { min: 3000, max: 15000, description: 'Pool heating system.' },
  
  // Storage
  'Built in robes': { min: 2000, max: 10000, description: 'Built-in wardrobes.' },
  'Walk in robe fit out': { min: 3000, max: 15000, description: 'Walk-in closet fitout.' },
  'Linen cupboard fit out': { min: 500, max: 2000, description: 'Linen storage shelving.' },
  'Garage shelving': { min: 500, max: 3000, description: 'Garage storage system.' },
  'Attic storage': { min: 2000, max: 8000, description: 'Attic access and storage.' },
  'Under stair storage': { min: 1000, max: 5000, description: 'Under-stair cupboard.' },
  'Garden shed': { min: 1000, max: 5000, description: 'Outdoor storage shed.' },
  'Workshop': { min: 5000, max: 25000, description: 'Workshop construction.' },
  'Carport': { min: 5000, max: 20000, description: 'Carport structure.' },
  
  // Window Furnishings
  'Blinds': { min: 2000, max: 10000, description: 'Window blinds package.' },
  'Curtains': { min: 2000, max: 12000, description: 'Curtains supply/install.' },
  'Plantation shutters': { min: 5000, max: 20000, description: 'Plantation shutters.' },
  
  // Finishing Touches
  'Security doors': { min: 500, max: 3000, description: 'Security screen doors.' },
  'Letterbox': { min: 100, max: 800, description: 'Mailbox installation.' },
  'Clothesline': { min: 200, max: 1000, description: 'Clothesline installation.' },
  
  // Moving & Insurance
  'Removalist costs': { min: 500, max: 5000, description: 'Moving/removalist fees.' },
  'Home insurance': { min: 1000, max: 4000, description: 'Annual home insurance.' },
  'Building insurance': { min: 1500, max: 5000, description: 'Construction insurance.' },
  'Maintenance allowance': { min: 2000, max: 10000, description: 'First year maintenance.' },
  
  // Custom
  'Custom item': { min: 100, max: 100000, description: 'Custom cost item.' },
};

